import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
    BackofficeAuditEntryDto,
    BackofficeAuditEntryPropertyChangeType,
    BackofficeInputDto,
    BackofficeMeInputDto,
    BackofficeService,
    PageableRequestDto,
    PagedResultDtoOfBackofficeAuditEntryDto,
    PermissionDto,
    Title
} from '../../../_services/configuration-services';
import {CssConstants, PermissionKeys} from '../../../app.constants';
import {faCalendar, faTimes} from '@fortawesome/free-solid-svg-icons';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {NotificationsService} from '../../../_shared/notifications.service';
import {ConfirmModalService} from '../../../_shared/_components/confirm-modal/confirm-modal.component';
import {NgbDateParserFormatter, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {FormUtils} from '../../../_shared/form-utils';
import {DateUtils} from '../../../_shared/date-utils';
import {CustomDateFormatter} from '../../../_shared/custom-date-formatter';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {TranslateUtils} from '../../../_shared/translate-utils';
import {MsalService} from '@azure/msal-angular';
import {environment} from '../../../../environments/environment';

interface BackofficeForm {
    title: FormControl<Title | null>;
    firstname: FormControl<string | null>;
    lastname: FormControl<string | null>;
    email: FormControl<string | null>;
    birthDate: FormControl<NgbDateStruct | null>;
    phone: FormControl<string | null>;
    mobilePhone: FormControl<string | null>;
    businessPhone: FormControl<string | null>;
    street: FormControl<string | null>;
    postalCode: FormControl<string | null>;
    city: FormControl<string | null>;
    country: FormControl<string | null>;
    accountEnabled?: FormControl<boolean | null>;
    comment?: FormControl<string | null>;
    permissions?: FormControl<PermissionDto[]>;
}

@Component({
    selector: 'app-backoffice-management',
    templateUrl: './backoffice-management.component.html',
    providers: [
        { provide: NgbDateParserFormatter, useClass: CustomDateFormatter }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BackofficeManagementComponent implements OnInit, OnDestroy {

    BackofficeAuditEntryPropertyChangeType = BackofficeAuditEntryPropertyChangeType;
    PermissionKeys = PermissionKeys;
    textInputPattern = FormUtils.textInputPattern;
    dateTimeFormat = DateUtils.dateTimeFormat;

    icons = {
        calendar: faCalendar,
        times: faTimes,
    };

    backofficeForm: FormGroup<BackofficeForm>;

    isEditMode = false;
    editingMyProfile = false;
    backofficeId: string | null = null;

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    pagedAudits: PagedResultDtoOfBackofficeAuditEntryDto | null = null;
    auditCurrentPage = 0;
    auditPageSize = 10;

    constructor(
        private readonly backofficeService: BackofficeService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly location: Location,
        private readonly notificationsService: NotificationsService,
        private readonly confirmService: ConfirmModalService,
        private readonly translateService: TranslateService,
        private readonly authService: MsalService) {

        this.backofficeForm = new FormGroup<BackofficeForm>({
            title: new FormControl<Title | null>(Title.NotSpecified, [Validators.required]),
            firstname: new FormControl<string | null>(null, [Validators.required]),
            lastname: new FormControl<string | null>(null, [Validators.required]),
            email: new FormControl<string | null>(null, [Validators.required, FormUtils.emailValidator]),
            birthDate: new FormControl<NgbDateStruct | null>(null, [FormUtils.datePatternValidator]),
            phone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
            mobilePhone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
            businessPhone: new FormControl<string | null>(null, [FormUtils.phoneNumberPatternValidator]),
            street: new FormControl<string | null>(null, [Validators.required]),
            postalCode: new FormControl<string | null>(null, [Validators.required]),
            city: new FormControl<string | null>(null, [Validators.required]),
            country: new FormControl<string | null>(null, [Validators.required, Validators.pattern(/^[a-zA-Z ]*$/)]),
            accountEnabled: new FormControl<boolean | null>(true, {validators: Validators.required}),
            comment: new FormControl<string | null>(null),
            permissions: new FormControl<PermissionDto[]>([], {nonNullable: true}),
        }, {updateOn: 'change'});
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.backofficeId = this.route.snapshot.params['id'] ?? null;
        if (this.backofficeId) { // Edit Backoffice (Admin)
            this.isEditMode = true;
            this.loadBackoffice(this.backofficeId);
        } else if (this.route.routeConfig?.path?.endsWith('create')) { // Create Backoffice (Admin)
            // No setup needed — form is constructed with defaults.
        } else { // Edit my profile
            this.editingMyProfile = true;
            this.isEditMode = true;
            this.loadMyProfile();
        }
    }

    private loadBackoffice(id: string): void {
        this.backofficeService.getBackoffice(id).subscribe(backoffice => {
            this.backofficeForm.patchValue({
                title: backoffice.title,
                firstname: backoffice.firstName,
                lastname: backoffice.lastName,
                email: backoffice.email,
                birthDate: DateUtils.dateToNgbDateStruct(backoffice.birthDate),
                phone: backoffice.phonePrivate,
                mobilePhone: backoffice.phoneMobile,
                businessPhone: backoffice.phoneProfessional,
                street: backoffice.street,
                postalCode: backoffice.postalCode,
                city: backoffice.city,
                country: backoffice.country,
                accountEnabled: backoffice.accountEnabled,
                comment: backoffice.comment,
                permissions: backoffice.permissions ?? [],
            });
        });
    }

    private loadMyProfile(): void {
        // Strip admin-only controls before the load: prevents the user from saving with
        // an empty form (and stale backofficeId=null) if the GET /me call errors.
        this.backofficeForm.removeControl('accountEnabled');
        this.backofficeForm.removeControl('comment');
        this.backofficeForm.removeControl('permissions');

        this.backofficeService.getMe().subscribe({
            next: me => {
                this.backofficeId = me.id;
                this.backofficeForm.patchValue({
                    title: me.title,
                    firstname: me.firstName,
                    lastname: me.lastName,
                    email: me.email,
                    birthDate: DateUtils.dateToNgbDateStruct(me.birthDate),
                    phone: me.phonePrivate,
                    mobilePhone: me.phoneMobile,
                    businessPhone: me.phoneProfessional,
                    street: me.street,
                    postalCode: me.postalCode,
                    city: me.city,
                    country: me.country,
                });
            },
            error: () => {
                this.notificationsService.error({title: 'common.errors.unexpectedError'});
                this.back();
            }
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
    }

    back(): void {
        this.location.back();
    }

    async save(): Promise<void> {
        if (!this.backofficeForm.valid) {
            window.scroll(0, 0);
            return;
        }
        if (this.editingMyProfile) {
            await this.saveMyProfile();
        } else if (this.isEditMode && this.backofficeId) {
            await this.saveBackofficeUpdate(this.backofficeId);
        } else {
            await this.saveBackofficeCreate();
        }
    }

    private async saveMyProfile(): Promise<void> {
        if (!this.backofficeId) {
            // Profile load did not complete — refuse to submit defaults that would wipe the user's data.
            return;
        }
        if (!await this.confirmReattributionIfNeeded()) {
            return;
        }
        await firstValueFrom(this.backofficeService.updateMe(this.buildMeDto(this.backofficeId)));
        this.notificationsService.success({title: 'leBackofficeAEteMisAJourAvecSucces'});
        this.backofficeForm.markAsPristine();
    }

    private async saveBackofficeUpdate(id: string): Promise<void> {
        if (!await this.confirmReattributionIfNeeded()) {
            return;
        }
        await firstValueFrom(this.backofficeService.updateBackoffice(id, this.buildBackofficeDto()));
        this.notificationsService.success({title: 'leBackofficeAEteMisAJourAvecSucces'});
    }

    private async saveBackofficeCreate(): Promise<void> {
        await firstValueFrom(this.backofficeService.createBackoffice(this.buildBackofficeDto()));
        this.notificationsService.success({title: 'leBackofficeAEteCreeAvecSucces'});
        this.back();
    }

    private async confirmReattributionIfNeeded(): Promise<boolean> {
        if (!(this.email.dirty && this.firstname.dirty && this.lastname.dirty)) {
            return true;
        }
        const result = await this.confirmService.confirm({
            titleKey: 'confirmezLaReattribution',
            messageKey: 'messages.confirmationReattributionCompteBackoffice'
        });
        return result === ConfirmModalService.yes;
    }

    private buildBirthDate(): Date | null {
        const birthDate = DateUtils.ngbDateStructToDate(this.birthDate.value);
        birthDate?.setHours(0, 0, 0, 0);
        return birthDate ?? null;
    }

    private buildMeDto(id: string): BackofficeMeInputDto {
        return new BackofficeMeInputDto({
            id,
            title: this.title.value as Title,
            firstName: this.firstname.value,
            lastName: this.lastname.value,
            email: this.email.value,
            birthDate: this.buildBirthDate(),
            street: this.street.value,
            postalCode: this.postalCode.value.toString(),
            city: this.city.value,
            country: this.country.value,
            phonePrivate: this.phone.value,
            phoneMobile: this.mobilePhone.value,
            phoneProfessional: this.businessPhone.value
        });
    }

    private buildBackofficeDto(): BackofficeInputDto {
        return new BackofficeInputDto({
            title: this.title.value as Title,
            firstName: this.firstname.value,
            lastName: this.lastname.value,
            email: this.email.value,
            birthDate: this.buildBirthDate(),
            street: this.street.value,
            postalCode: this.postalCode.value.toString(),
            city: this.city.value,
            country: this.country.value,
            phonePrivate: this.phone.value,
            phoneMobile: this.mobilePhone.value,
            phoneProfessional: this.businessPhone.value,
            accountEnabled: this.accountEnabled.value,
            comment: this.comment.value,
            permissions: this.permissions.value
        });
    }

    resetPassword(): void {
        const passwordResetUrl = `https://${environment.b2cDomainName}.b2clogin.com/${environment.b2cDomainName}.onmicrosoft.com/${environment.b2cResetPasswordPolicy}`;
        this.authService.loginRedirect({authority: passwordResetUrl, scopes: []});
    }

    async setAuditsPage(page: any): Promise<void> {
        this.auditCurrentPage = page.offset;
        await this.fetchAudits();
    }

    async fetchAudits(): Promise<void> {
        if (!this.backofficeId) {
            return;
        }
        this.pagedAudits = await firstValueFrom(this.backofficeService.getBackofficeAuditEntries(
            this.backofficeId,
            new PageableRequestDto({page: this.auditCurrentPage + 1, pageSize: this.auditPageSize})
        ));
    }

    getRowClass(row: BackofficeAuditEntryDto): string {
        return row.changeType === BackofficeAuditEntryPropertyChangeType.PropertyChange && row.properties.length > 1
            ? CssConstants.max150pxRowHeightClass
            : CssConstants.unlimitedRowHeightClass;
    }

    async deleteBackoffice(): Promise<void> {
        const result = await this.confirmService.confirm({
            titleKey: 'common.confirmModal.title.delete',
            messageKey: 'messages.avertissementSuppressionCompteBackoffice'
        });
        if (result !== ConfirmModalService.yes) {
            return;
        }
        await firstValueFrom(this.backofficeService.deleteBackoffice(this.backofficeId!));
        this.notificationsService.success({ title: 'leBackofficeAEteSupprimeAvecSucces' });
        void this.router.navigate(['users/backoffices']);
    }

    // Form accessors

    get title(): FormControl<Title | null> { return this.backofficeForm.controls.title; }
    get firstname(): FormControl<string | null> { return this.backofficeForm.controls.firstname; }
    get lastname(): FormControl<string | null> { return this.backofficeForm.controls.lastname; }
    get email(): FormControl<string | null> { return this.backofficeForm.controls.email; }
    get birthDate(): FormControl<NgbDateStruct | null> { return this.backofficeForm.controls.birthDate; }
    get phone(): FormControl<string | null> { return this.backofficeForm.controls.phone; }
    get mobilePhone(): FormControl<string | null> { return this.backofficeForm.controls.mobilePhone; }
    get businessPhone(): FormControl<string | null> { return this.backofficeForm.controls.businessPhone; }
    get street(): FormControl<string | null> { return this.backofficeForm.controls.street; }
    get postalCode(): FormControl<string | null> { return this.backofficeForm.controls.postalCode; }
    get city(): FormControl<string | null> { return this.backofficeForm.controls.city; }
    get country(): FormControl<string | null> { return this.backofficeForm.controls.country; }
    get accountEnabled(): FormControl<boolean | null> { return this.backofficeForm.controls.accountEnabled; }
    get comment(): FormControl<string | null> { return this.backofficeForm.controls.comment; }
    get permissions(): FormControl<PermissionDto[]> { return this.backofficeForm.controls.permissions; }
}
